import {
  faCircle as faCircleSolid,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import { decimalPlaces, useLocaleContext } from "../contexts/LocaleContext";

import { faCircle } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { LengthUnits } from "convert-units/definitions/length";
import { SpeedUnits } from "convert-units/definitions/speed";
import { scaleThreshold } from "d3-scale";
import { observer } from "mobx-react-lite";
import { Icon } from "trunx";
import config from "../config";
import convert from "../convert";
import { BaseMap } from "../models/Layers";
import { useRootStore } from "../models/Root";
import { ISensorDisplayPrecip } from "../models/SensorDisplay";

const markerColor = (map: BaseMap) =>
  map.startsWith("Imagery") ? "white" : "black";

const { breakValues: precipDotBreakValues, colors: precipDotColors } =
  config.markers.precip.dot;
const precipDotColor = scaleThreshold(precipDotBreakValues, precipDotColors);

export const colorForPrecipValue = (value: number) => precipDotColor(value);

export const PrecipNone = observer(() => {
  const {
    layers: { baseMap },
  } = useRootStore();

  const color = markerColor(baseMap);
  return (
    <Icon style={{ fontSize: "1.2em" }}>
      <span className="fa-layers">
        <FontAwesomeIcon icon={faCircleSolid} color={color} opacity={0.1} />
        <FontAwesomeIcon
          icon={faCircle}
          color={color}
          opacity={0.8}
          className="border"
        />
      </span>
    </Icon>
  );
});

export const PrecipM = observer(({ size = 14 }: { size?: number }) => {
  const {
    layers: { baseMap },
  } = useRootStore();

  let color: string;
  let opacity: number;
  if (config.markers.missing) {
    color = config.markers.missing;
    opacity = 1;
  } else {
    color = markerColor(baseMap);
    opacity = 0.8;
  }

  return (
    <Icon style={{ fontSize: `${size}px` }} className="missing">
      <FontAwesomeIcon
        icon={faExclamationTriangle}
        color={color}
        opacity={opacity}
      />
    </Icon>
  );
});

const PrecipDot = ({ valueForColor }: { valueForColor: number }) => {
  return (
    <Icon style={{ fontSize: "1.2em" }} className="fa-layers">
      <span className="fa-layers">
        <FontAwesomeIcon
          icon={faCircleSolid}
          color={colorForPrecipValue(valueForColor)}
        />
        <FontAwesomeIcon icon={faCircle} color={"black"} className="border" />
      </span>
    </Icon>
  );
};
export const PrecipTag = ({
  value,
  valueForColor,
}: {
  value: string;
  valueForColor: number;
}) => {
  const { breakValues, backgroundColors, textColors } =
    config.markers.precip.tag;
  const backgroundColor = scaleThreshold(breakValues, backgroundColors);
  const textColor = scaleThreshold(breakValues, textColors);

  return (
    <span
      className="tag sensor-marker precip-tag sensor-tag"
      style={{
        backgroundColor: backgroundColor(valueForColor),
        color: textColor(valueForColor),
      }}
    >
      {value}
    </span>
  );
};

interface PrecipMarkerProps {
  measure: ISensorDisplayPrecip["measure"];
  depth: number | null;
  intensity: number | null;
  depthUnit: LengthUnits;
  intensityUnit: SpeedUnits;
}

const PrecipMarker = ({
  measure,
  depth,
  intensity,
  depthUnit: fromDepthUnit,
  intensityUnit: fromIntensityUnit,
}: PrecipMarkerProps) => {
  const {
    units: {
      precip: { total: toTotalUnit, intensity: toIntensityUnit },
    },
  } = useLocaleContext();

  const {
    map: { zoom },
  } = useRootStore();

  const { dotDisplayZoomLevel } = config.markers.precip;

  const value = measure === "total" ? depth : intensity;
  const isNull = value === null;
  if (isNull || (value === 0 && dotDisplayZoomLevel)) {
    return <PrecipNone />;
  }

  const fromUnit = measure === "total" ? fromDepthUnit : fromIntensityUnit;
  const valueForColorUnit = measure === "total" ? "in" : "in/h";
  const valueForColor = convert(value).from(fromUnit).to(valueForColorUnit);

  if (zoom && dotDisplayZoomLevel && zoom <= dotDisplayZoomLevel) {
    return <PrecipDot valueForColor={valueForColor}></PrecipDot>;
  }

  const toUnit = measure === "total" ? toTotalUnit : toIntensityUnit;
  const value_ = convert(value)
    .from(fromUnit)
    .to(toUnit)
    .toFixed(decimalPlaces(toUnit));

  return <PrecipTag valueForColor={valueForColor} value={value_}></PrecipTag>;
};

export default observer(PrecipMarker);
